import type {
  CommentMessage,
  CommentPerson,
  CommentThread,
  CommentThreadDetail,
} from "@/domain/comment";
import {
  commandThread,
  type CommentReplyCommand,
  type CommentThreadCommand,
} from "./commentCommand";

export type CommentEdit = CommentThreadCommand | CommentReplyCommand;

function editedMessage(message: CommentMessage, command: CommentEdit, now: string): CommentMessage {
  if (!("thread" in command) || message.uuid !== command.uuid) return message;
  if (command.action === "editMessage") return { ...message, body: command.body, edited_at: now };
  if (command.action === "deleteMessage") return { ...message, deleted_at: now };
  const present = message.reactions.find((reaction) => reaction.emoji === command.emoji);
  const added = command.action === "react";
  const count = (present?.count ?? 0) + (added ? (present?.me ? 0 : 1) : present?.me ? -1 : 0);
  const reactions = message.reactions.filter((reaction) => reaction.emoji !== command.emoji);
  if (count > 0) reactions.push({ emoji: command.emoji, count, me: added });
  return { ...message, reactions };
}

/** The thread as its list row shows it once the action lands. */
export function optimisticThread<T extends CommentThread>(
  thread: T,
  command: CommentEdit,
  actor: CommentPerson | null,
): T {
  if (thread.uuid !== commandThread(command)) return thread;
  const now = new Date().toISOString();
  switch (command.action) {
    case "resolve":
      return { ...thread, resolved_at: now, resolved_by: actor };
    case "reopen":
      return { ...thread, resolved_at: null, resolved_by: null };
    case "mute":
      return { ...thread, muted: true };
    case "unmute":
      return { ...thread, muted: false };
    case "reply":
      return { ...thread, reply_count: thread.reply_count + 1, last_message_at: now };
    default: {
      const root = thread.root_message && editedMessage(thread.root_message, command, now);
      return root === thread.root_message ? thread : { ...thread, root_message: root };
    }
  }
}

/** The open thread with its messages once the action lands. */
export function optimisticCommentThread(
  thread: CommentThreadDetail,
  command: CommentEdit,
  actor: CommentPerson | null,
): CommentThreadDetail {
  if (thread.uuid !== commandThread(command)) return thread;
  const next = optimisticThread(thread, command, actor);
  if (command.action === "reply")
    return {
      ...next,
      messages: [
        ...thread.messages,
        {
          uuid: `optimistic-${thread.uuid}`,
          author: actor,
          body: command.body,
          created_at: next.last_message_at,
          edited_at: null,
          deleted_at: null,
          mentions: [],
          reactions: [],
        },
      ],
    };
  if (!("thread" in command)) return next;
  const now = new Date().toISOString();
  return {
    ...next,
    messages: thread.messages.map((message) => editedMessage(message, command, now)),
  };
}
